import React, { useState } from "react";
import Button from "../Button";
import CheckBox from "../CheckBox";
import { Dropdown } from "../Dropdown";
import TextInput from "../TextInput";
import InlineAlert from "../InlineAlert";
import { getTitleForWorkspacePlan } from "../../../routes/workspace/utils";
import { WorkspaceUsage } from "../../../../state/types";
import { isFreeOrTrialPlan } from "@functions/workspacePlans";
import { twClassNames } from "../../helpers/twClassNames";

const DOWNGRADE_REASONS = [
    { text: "I didn't find features I need", value: "didnt_find_features_i_need" },
    { text: "I need more time to experiment", value: "need_more_time_to_experiment" },
    { text: "The paid plan is too expensive", value: "starter_plan_too_expensive" },
    { text: "I'm happy with the downgraded version", value: "happy_with_downgraded_version" },
    { text: "Other (Please explain below)", value: "other_please_explain_below" }
];

export const DowngradePlanSurveyDialog = ({
    selectedPlan,
    onUndoClick,
    handleClose,
    subscriptionEndDate,
    currentPlan,
    frequency,
    expiredTrial,
    usage
}: {
    selectedPlan: "sandbox" | "public" | "basic" | "core"; // TODO: Centralize in `@functions/plans`
    onUndoClick?: () => void | null;
    handleClose: () => void;
    subscriptionEndDate: string;
    currentPlan: string;
    frequency: { interval: string; labelWithoutDiscount: string };
    expiredTrial: boolean;
    usage: WorkspaceUsage;
}) => {
    const [downgradeReason, setDowngradeReason] = useState<
        { text: string; value: string } | undefined
    >(undefined);
    const [downgradeReasonExplanation, setDowngradeReasonExplanation] = useState("");
    const [publicConfirmed, setPublicConfirmed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const downgradeOnSubscriptionEnd = !isFreeOrTrialPlan(usage);

    const handleDowngradePlan = async () => {
        setIsProcessing(true);
        const eventName = expiredTrial
            ? "Trial Downgrade Acknowledged"
            : "Workspace Switched to " + capitalize(selectedPlan);
        await eventTracker.event({
            type: eventName,
            data: {
                plan: selectedPlan,
                currentPlan: usage?.plan,
                downgradeReason: downgradeReason?.value,
                downgradeReasonExplanation,
                trialType: usage?.features?.creditSystem ? "Growth UBP" : "Starter"
            }
        });
        handleClose();
        setIsProcessing(false);
    };

    // TODO: Centralize in `@functions/plans` and/or `@functions/featureAvailability`
    const showFrequency = selectedPlan === "basic" || selectedPlan === "core";
    const showPublicCheckbox = selectedPlan === "public" && !usage?.features?.allowPrivateProjects;

    return (
        <div className="flex h-full w-full items-center justify-center">
            <div className="flex items-center justify-center">
                <div
                    className={twClassNames(
                        "flex max-w-xl flex-col justify-center gap-6 rounded-lg border-1 bg-white shadow-lg",
                        expiredTrial ? "p-8" : "p-4"
                    )}
                >
                    <div className="mt-2 flex flex-col gap-3">
                        <div className="flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-100 text-pink-700">
                                <i className="far fa-heart-broken pt-1 text-xl text-pink-700"></i>
                            </div>
                        </div>

                        <span className="text-center text-lg font-semibold text-gray-900">
                            Your plan has been downgraded.
                        </span>
                    </div>

                    <div className="flex flex-col gap-4">
                        <span className="text-center text-base text-gray-700">
                            You'll{" "}
                            <span className="font-semibold">
                                retain access to your workspace and images
                            </span>{" "}
                            but have lower limits, limited features, and no priority support.
                        </span>

                        {/* Show this message if the user is manually downgrading, not if their trial has already expired */}
                        {!expiredTrial && usage?.plan === "trial" && (
                            <InlineAlert level="danger" className="py-2">
                                Your Roboflow Trial will end immediately
                            </InlineAlert>
                        )}

                        {downgradeOnSubscriptionEnd && (
                            <InlineAlert level="danger" className="py-2">
                                <span>
                                    Your {getTitleForWorkspacePlan(currentPlan)} will be downgraded
                                    to the{" "}
                                    {showFrequency ? (
                                        <span className="inline font-bold">
                                            {frequency.labelWithoutDiscount}
                                        </span>
                                    ) : null}{" "}
                                    {getTitleForWorkspacePlan(selectedPlan)} on{" "}
                                    {subscriptionEndDate}.
                                </span>
                            </InlineAlert>
                        )}

                        <div>
                            <div className="my-1 w-full text-sm font-medium text-gray-700">
                                Why are you downgrading? <span className="text-red-500">*</span>
                            </div>
                            <Dropdown
                                items={DOWNGRADE_REASONS}
                                onItemSelected={setDowngradeReason}
                                theme="light"
                                title=""
                                fullWidth={true}
                                titlePrefix={downgradeReason?.text ?? "Please select a reason"}
                            />
                        </div>

                        <TextInput
                            onChange={setDowngradeReasonExplanation}
                            value={downgradeReasonExplanation}
                            label="Anything else you'd like to share?"
                            placeholder="Type your answer here"
                            multiline={3}
                        />

                        {showPublicCheckbox && (
                            <CheckBox
                                checked={publicConfirmed}
                                onChange={() => {
                                    setPublicConfirmed(!publicConfirmed);
                                }}
                                label="I understand that all existing projects and models will be public."
                            />
                        )}
                    </div>

                    <div className="flex justify-between gap-2">
                        {onUndoClick && (
                            <Button
                                className="flex-1"
                                type="secondary"
                                onClick={async () => {
                                    setIsProcessing(true);
                                    await onUndoClick();
                                    setIsProcessing(false);
                                }}
                                loading={isProcessing}
                            >
                                Undo
                            </Button>
                        )}
                        <Button
                            className="flex-1"
                            type="primary"
                            onClick={handleDowngradePlan}
                            disabled={!downgradeReason || (showPublicCheckbox && !publicConfirmed)}
                            loading={isProcessing}
                        >
                            Continue
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
};
